import axios from "axios"
import get from "lodash/get"
import isEmpty from "lodash/isEmpty"
import toLower from "lodash/toLower"
import { urls } from "utils-lib/builds"
import { resendEmail } from "actions-lib/resendEmail"
import { userType } from "constants-lib/authentication"
import {
  getSessionTokenFromData,
  getLoginStatus,
  getExceptionLoginStatus,
  getUserDetailsFromData,
  loginStatusCode,
  logRocketIdentifyFailedLoggedInUser,
  logRocketIdentifyLoggedInUser,
  redirectLoggedInUser,
} from "utils-lib/userAccount/login"
import { tagLoginFailure, tagLoginSuccess } from "utils-lib/analytics/login"
import { setToken } from "actions-lib/setToken"
import { setUserState } from "actions-lib/setUserState"
import { setUserDetails } from "actions-lib/setUserDetails"
import { clearNotifications } from "actions-lib/clearNotifications"
import { getUserPersonlization } from "actions-lib/getUserPersonalization"
import { authClient } from "constants-lib/okta"
import {
  AUTHENTICATE_USER_PENDING,
  AUTHENTICATE_USER_FULFILLED,
  AUTHENTICATE_USER_FAILED,
} from "constants-lib/actionTypes"
import { getWindow } from "utils-lib/getWindow"
import {
  pushToAdobeDataLayer,
  updateLoggedInFailure,
} from "utils-lib/analytics/adobeDataLayer"
import { PREPAYMENT_YES } from "constants-lib/common"
import { isNextApp } from "utils-lib/isNextApp"
import { getPersistor } from "utils-lib/persistorRef/persistorRef"

const ssrWindow = getWindow()

export const pendingAC = () => ({
  type: AUTHENTICATE_USER_PENDING,
})

export const failedAC = (status) => ({
  type: AUTHENTICATE_USER_FAILED,
  payload: status,
})

export const fulfilledAC = (data) => ({
  type: AUTHENTICATE_USER_FULFILLED,
  payload: data,
})

const handleNextAppRedirect = async (locale, redirectUrl) => {
  // ensures the state is written to storage before redirecting, prevents hydration issues on the next page load
  // this resolves some hydration edge cases with the user account state (logged in vs logged out) when navigating with browser back button back from MyWM
  await getPersistor().flush()

  redirectLoggedInUser(locale, redirectUrl)
}

/*
  Verifies that the username (email) and password are valid.
  If so, pass the returned session token to getWithoutPrompt to:
    1. Obtain an access token, and
    2. Begin a session in the browser (session cookie will get generated)
  After starting the session, redirect the user to the authenticated page.
*/
/**
 * Authenticates a user and dispatches actions based on the response.
 * @param {Object} options - Options for the authentication process.
 * @param {Function} [options.callback=() => {}] - Function to be called after authentication, defaults to a no-op function.
 * @param {boolean} [options.disablePostLoginRedirect=false] - Flag to disable redirection after login, defaults to false.
 * @param {string} [options.redirectUrl=""] - URL to redirect to after login, defaults to an empty string.
 * @param {string} options.username - Username for authentication.
 * @param {string} options.password - Password for authentication.
 * @param {Function} [options.beforeRedirect] - Function to be called before redirection, if redirection is enabled.
 * @returns {Promise<Object>} A promise that resolves to an object containing the authentication outcome.
 */
export const authenticateUser =
  ({
    callback = () => {},
    disablePostLoginRedirect = false,
    redirectUrl = ``,
    LDFFReleaseHandleAutopayEnrollmentFailureAfterGuestOneTimePayment = false,
    ...options
  }) =>
  async (dispatch, getState) => {
    dispatch(pendingAC())

    const api = `AUTHENTICATE_USER`
    const url = urls.url[api]
    const apiKey = get(urls, `apiKey.USER[${api}]`, ``)
    const state = getState()
    const locale = get(state, `siteLanguage.language`, `en_US`)
    const LOGIN_SUCCESS_EVENTNAME = `loginSuccess`
    const LOGIN_SUCCESS_EVENTNAME_SUPPORT_PAGE = `sct-req-support-login-success`

    const headers = {
      "Content-Type": `application/json`,
      apiKey,
    }

    let response = {}
    try {
      /* 
      This has been refactored to use async/await because of syncronous issues with existing code. 
      An edge case scenario that users were running into is that they were receiving the "technical difficulties"
      error message when the auth request succeeded with a 200 because the status code was === UNKNOWN_LOGIN_STATUS: 154.
      To fix this, we await the response, and then we await the loginStatus before we do anything with it.
    */
      response = await axios.post(
        url,
        {
          username: options.username,
          password: options.password,
          locale,
        },
        { headers },
      )
    } catch (error) {
      const loginStatus = getExceptionLoginStatus(error)
      tagLoginFailure(loginStatus, options.username, error)

      dispatch(failedAC(loginStatus))
      callback(loginStatus)
      updateLoggedInFailure()
      return loginStatus
    }

    await dispatch(fulfilledAC(response.data))

    const loginStatus = await getLoginStatus(response)

    if (loginStatusCode.SUCCESS === loginStatus) {
      await logRocketIdentifyLoggedInUser(response)

      /* User entered valid credentials, so start Okta session by getting an access token. */
      const sessionToken = await getSessionTokenFromData(response)

      try {
        const token = await authClient.token.getWithoutPrompt({
          responseType: `token`,
          sessionToken,
        })

        // Okta token object has changed the structure, but to be safe we are checking the old structure as well in case this changes again
        const accessToken = !isEmpty(token?.accessToken)
          ? token
          : token?.tokens?.accessToken

        const loginSuccessData = {
          visitor: {
            isLoggedIn: toLower(PREPAYMENT_YES),
          },
        }

        await dispatch(
          () =>
            new Promise((resolve) => {
              pushToAdobeDataLayer(
                options?.supportLogin
                  ? LOGIN_SUCCESS_EVENTNAME_SUPPORT_PAGE
                  : LOGIN_SUCCESS_EVENTNAME,
                loginSuccessData,
              )
              resolve()
            }),
        )

        await tagLoginSuccess(loginStatus, response)

        await dispatch(setToken(accessToken))
        await dispatch(
          setUserDetails(getUserDetailsFromData(response, getState())),
        )
        await dispatch(setUserState(userType.LOGGING_IN))

        await dispatch(clearNotifications())
        const personalizationAPIData = await dispatch(getUserPersonlization())
        const additionalData = {
          clicks: {
            object_content: {
              personalizationAPIData,
            },
          },
        }
        pushToAdobeDataLayer("personalizationAPI", additionalData)

        /* Todo - update app to handle logged in state without relying on a page reload. */
        if (disablePostLoginRedirect) {
          const isPrefCenterOrManageContacts =
            /.+\/mywm\/user\/(contacts\/verify|preferences\/search)/.test(
              ssrWindow.location.pathname,
            )
          // behavior is different for pref center and manage contacts login, so make sure we are able to fetch linked accounts without a page refresh
          if (isPrefCenterOrManageContacts) {
            await dispatch(setUserState(userType.LOGGED_IN))
          } else {
            ssrWindow.location.reload()
          }
        } else if (options.beforeRedirect) {
          const beforeRedirectResult = await options.beforeRedirect()
          const finalRedirectUrl =
            typeof beforeRedirectResult === `string` && beforeRedirectResult
              ? beforeRedirectResult
              : redirectUrl

          if (isNextApp) {
            dispatch(setUserState(userType.LOGGED_IN))
            handleNextAppRedirect(
              locale,
              LDFFReleaseHandleAutopayEnrollmentFailureAfterGuestOneTimePayment
                ? finalRedirectUrl
                : redirectUrl,
            )
          } else {
            redirectLoggedInUser(
              locale,
              LDFFReleaseHandleAutopayEnrollmentFailureAfterGuestOneTimePayment
                ? finalRedirectUrl
                : redirectUrl,
            )
          }
        } else if (isNextApp) {
          dispatch(setUserState(userType.LOGGED_IN))
          handleNextAppRedirect(locale, redirectUrl)
        } else {
          redirectLoggedInUser(locale, redirectUrl)
        }
      } catch (error) {
        await tagLoginFailure(
          loginStatusCode.GET_WITHOUT_PROMPT_FAIL,
          options.username,
          error,
        )
        callback(loginStatusCode.GET_WITHOUT_PROMPT_FAIL)
        updateLoggedInFailure()
      }
    } else {
      await logRocketIdentifyFailedLoggedInUser(options.username)

      if (loginStatusCode.UNCONFIRMED_EMAIL === loginStatus) {
        /* User hasn't confirmed their email, so resend the email.
          User Authenticate API returns this status whether or not the user entered PW is correct.
          Do not resend the email if user is just toggling the language setting. */
        await dispatch(
          resendEmail({
            profile: {
              login: options.username,
            },
            locale,
          }),
        )
      }

      updateLoggedInFailure()
      await callback(loginStatus)
    }

    return loginStatus
  }
